import React from 'react';
import {useTranslation} from 'react-i18next';

import {GROUPING_ONE_PER_ACCOUNT, GROUPING_PROPOSED} from '../wizardReducer';

/**
 * «Agrupadas como propone Dashbo» ⇄ «Cada cuenta por separado».
 *
 * Es la única pregunta del recorrido que el usuario contesta con certeza y el algoritmo nunca. La
 * agrupación por nombre es **bimodal**: medida contra producción, 34 de 62 agencias quedan en 0,80
 * de precisión o más —diez de ellas en 1,00— y el resto agrupa mal. Lo que separa los dos mundos es
 * si la agencia tiene una cuenta por cliente o varias, y eso no está en ningún dato que podamos
 * mirar: lo sabe la agencia. Un clic le entrega la decisión.
 *
 * No aparece si no hay ningún grupo de varias cuentas: sin merges que deshacer, el control no
 * ofrece nada.
 *
 * El estado activo lleva las tres señales de la convención del repo —relleno pleno, tilde en lugar
 * del ícono propio, y el verbo en el label—: un tinte lavado no se lee, menos en un screenshot.
 */
export function GroupingToggle({grouping, groupCount, liveGroupCount, onChange}) {
    const {t} = useTranslation();
    if (!groupCount) return null;

    const grouped = grouping !== GROUPING_ONE_PER_ACCOUNT;
    /**
     * Cuántos junta la lista que el usuario TIENE DELANTE, no la propuesta original.
     *
     * `groupCount` sale del servidor y es fijo; la lista viva cambia cuando el usuario reagrupa. Con
     * el fijo, el botón decía «(25 con varias cuentas)» arriba de un renglón que decía «27 agrupan
     * varias cuentas» — un subconjunto mayor que su total.
     *
     * Sigue existiendo el fijo, y hace falta: es el que decide si el control se dibuja. En el modo
     * «cada cuenta por separado» la lista viva no tiene un solo grupo múltiple, así que mostrar ahí
     * el vivo sería «(0 con varias cuentas)» y —peor— con el fijo fuera del gate el control
     * desaparecería justo cuando hay que volver a agrupar.
     */
    const shown = grouped && typeof liveGroupCount === 'number' ? liveGroupCount : groupCount;

    return (
        <div className="ob-grouping" role="group" aria-label={t('onboarding.clients.grouping_label')}>
            <button
                type="button"
                className={'ob-grouping__btn' + (grouped ? ' -active' : '')}
                aria-pressed={grouped}
                onClick={() => onChange(GROUPING_PROPOSED)}
            >
                {grouped && <span className="ob-grouping__tick" aria-hidden="true">✓</span>}
                {t('onboarding.clients.grouping_proposed', {count: shown})}
            </button>
            <button
                type="button"
                className={'ob-grouping__btn' + (!grouped ? ' -active' : '')}
                aria-pressed={!grouped}
                onClick={() => onChange(GROUPING_ONE_PER_ACCOUNT)}
            >
                {!grouped && <span className="ob-grouping__tick" aria-hidden="true">✓</span>}
                {t('onboarding.clients.grouping_one_per_account')}
            </button>
        </div>
    );
}

/** Cuántos grupos de VARIAS cuentas propuso Dashbo. Los de una no se pueden separar. */
export function multiAccountGroupCount(proposals) {
    return (proposals?.proposed_clients || []).filter(group => (group.accounts || []).length >= 2).length;
}

export default GroupingToggle;
